import { useEffect, useMemo, useState } from 'react';
import type { ElementType } from 'react';
import { createPortal } from 'react-dom';
import {
  Globe, Mail, Monitor, Smartphone, AtSign, Cloud,
  Sparkles, CheckCircle2, Circle, AlertTriangle, Info,
} from 'lucide-react';
import { GmailIcon, AppleIcon } from './brand-icons';
import { substituteVariables } from './ftp-editor-utils';
import './ftp-editor.css';

export interface ClientUnsupported {
  msoConditionals?: boolean;
  stripStyles?: boolean;
  stripClassId?: boolean;
  stripMediaQueries?: boolean;
  stripFontFace?: boolean;
  stripForms?: boolean;
  stripProps?: string[];
}

export interface ClientDef {
  id: string;
  label: string;
  /**
   * The real brand mark where one is available (see brand-icons.tsx); a plain
   * glyph otherwise. Either way the per-client accent colour in ftp-editor.css
   * tints it, so the tabs are colour-coded by service.
   */
  Icon: ElementType;
  unsupported: ClientUnsupported;
}

const CLIENTS: ClientDef[] = [
  { id: 'browser', label: 'Browser', Icon: Globe, unsupported: {} },
  {
    id: 'gmail',
    label: 'Gmail',
    Icon: GmailIcon,
    unsupported: {
      stripStyles: true,
      stripClassId: true,
      stripMediaQueries: true,
      stripFontFace: true,
      stripForms: true,
      stripProps: ['position', 'float'],
    },
  },
  {
    id: 'outlook',
    label: 'Outlook',
    Icon: Mail,
    unsupported: {
      msoConditionals: true,
      stripMediaQueries: true,
      stripFontFace: true,
      // NOTE: `max-width` is intentionally NOT stripped. Real Outlook caps
      // width via the `[if mso]` ghost tables + `width` attributes this compiler
      // emits; the browser sim drops those ghost tables, so stripping max-width
      // too would leave content full-width (worse than real Outlook). Keeping it
      // makes the tab show the real ~600px content width.
      stripProps: [
        'border-radius',
        'box-shadow',
        'max-height',
        'background-image',
        'position',
        'float',
        'opacity',
        'flex',
        'grid',
        'animation',
        'transform',
      ],
    },
  },
  { id: 'apple', label: 'Apple Mail', Icon: AppleIcon, unsupported: {} },
  {
    id: 'yahoo',
    label: 'Yahoo',
    Icon: AtSign,
    unsupported: {
      stripStyles: true,
      stripClassId: true,
      stripMediaQueries: true,
      stripFontFace: true,
      stripForms: true,
      stripProps: ['position'],
    },
  },
  {
    id: 'outlookcom',
    label: 'Outlook.com',
    Icon: Cloud,
    unsupported: {
      stripStyles: true,
      stripFontFace: true,
      stripForms: true,
      stripProps: ['position'],
    },
  },
];

const OUTLOOK_SIM_CSS =
  '<style id="ftp-outlook-sim">' +
  '*{border-radius:0!important;box-shadow:none!important;animation:none!important;transform:none!important;opacity:1!important}' +
  'img{outline:1px solid #ccc;border:0}' +
  '[style*="flex"],[style*="display:flex"],[style*="display: flex"]{display:block!important}' +
  '[style*="grid"],[style*="display:grid"],[style*="display: grid"]{display:block!important}' +
  '</style>';

function processMsoHtml(html: string): string {
  // Approximate Outlook inside a browser iframe. A browser cannot render VML
  // (<v:roundrect>/<v:rect>) or Office islands (<o:OfficeDocumentSettings> —
  // whose "96" would otherwise show as literal text at the top of the email),
  // so we must NOT expose [if mso] regions. Instead:
  //   1. reveal the [if !mso] fallback (the browser-renderable version of the
  //      element — e.g. the real button <a>), so content does not disappear;
  //   2. drop the Outlook-only regions entirely (ghost tables, VML, PixelsPerInch).
  // The stripProps + OUTLOOK_SIM_CSS pass then approximates Outlook's lack of
  // border-radius / flex / etc. (The previous implementation did the opposite —
  // un-commenting [if mso] and deleting [if !mso] — which produced the phantom
  // "96" and made buttons/content vanish in the Outlook tab.)
  let s = html;
  // 1. Reveal downlevel-revealed `[if !mso]><!-- ... <!--<![endif]` content.
  s = s.replace(/<!--\s*\[if\s+!mso\]>\s*(?:<!-->)?/gi, '');
  s = s.replace(/<!--\s*<!\[endif\]-->/gi, '');
  // 2. Remove genuine Outlook-only conditional regions (never negated `!mso`).
  s = s.replace(/<!--\s*\[if\s+(?!!)[^\]]*mso[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '');
  return s;
}

// Remove every `@word ... { ... }` block — INCLUDING its nested rules — by
// brace-counting. A regex cannot reliably match the nested braces of a media
// block that contains multiple rules; the previous regex stripped the `@media`
// wrapper but LEFT its inner rules (e.g. `.dnd-column-stack { display:block }`)
// behind as unconditional styles, which forced columns to stack in every
// simulated client (Gmail/Yahoo/Outlook/Outlook.com) while Browser/Apple —
// which keep the intact media query — rendered correctly.
function removeAtBlocks(css: string, atWord: string): string {
  const lower = css.toLowerCase();
  const needle = atWord.toLowerCase();
  let out = '';
  let i = 0;
  while (i < css.length) {
    const at = lower.indexOf(needle, i);
    if (at === -1) {
      out += css.slice(i);
      break;
    }
    const open = css.indexOf('{', at);
    if (open === -1) {
      out += css.slice(i);
      break;
    }
    let depth = 1;
    let k = open + 1;
    for (; k < css.length && depth > 0; k++) {
      const ch = css[k];
      if (ch === '{') depth++;
      else if (ch === '}') depth--;
    }
    out += css.slice(i, at); // keep everything before the at-rule
    i = k; // skip the whole (balanced) block
  }
  return out;
}

function stripAtRulesFromStyleCss(css: string, stripFontFace: boolean, stripMedia: boolean): string {
  let out = css;
  if (stripFontFace) out = removeAtBlocks(out, '@font-face');
  if (stripMedia) out = removeAtBlocks(out, '@media');
  return out;
}

function parseDeclarations(decl: string): [string, string][] {
  const pairs: [string, string][] = [];
  let depth = 0;
  let cur = '';
  for (let i = 0; i < decl.length; i++) {
    const ch = decl[i];
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ';' && depth === 0) {
      const part = cur.trim();
      if (part) {
        const idx = part.indexOf(':');
        if (idx > -1) pairs.push([part.slice(0, idx).trim(), part.slice(idx + 1).trim()]);
      }
      cur = '';
      continue;
    }
    cur += ch;
  }
  if (cur.trim()) {
    const part = cur.trim();
    const idx = part.indexOf(':');
    if (idx > -1) pairs.push([part.slice(0, idx).trim(), part.slice(idx + 1).trim()]);
  }
  return pairs;
}

function mergeInlineStyle(existing: string | null, prop: string, value: string): string {
  const map = new Map<string, string>();
  if (existing) {
    for (const [p, v] of parseDeclarations(existing)) map.set(p.toLowerCase(), `${p}: ${v}`);
  }
  map.set(prop.toLowerCase(), `${prop}: ${value}`);
  return `${Array.from(map.values()).join('; ')};`;
}

function removePropsFromStyle(styleAttr: string | null, strip: string[]): string | null {
  if (!styleAttr || !strip.length) return styleAttr;
  const lower = new Set(strip.map((p) => p.toLowerCase()));
  const kept: string[] = [];
  for (const [prop, val] of parseDeclarations(styleAttr)) {
    if (!lower.has(prop.toLowerCase())) kept.push(`${prop}: ${val}`);
  }
  if (!kept.length) return null;
  return `${kept.join('; ')};`;
}

function extractCssRules(css: string): { selectors: string[]; declarations: string }[] {
  const rules: { selectors: string[]; declarations: string }[] = [];
  const noComments = css.replace(/\/\*[\s\S]*?\*\//g, '');
  const parts = noComments.split('}');
  for (const part of parts) {
    const idx = part.indexOf('{');
    if (idx === -1) continue;
    const selPart = part.slice(0, idx).trim();
    const decl = part.slice(idx + 1).trim();
    if (!selPart || !decl || selPart.includes('@')) continue;
    const selectors = selPart
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s && !s.startsWith('@'));
    if (selectors.length) rules.push({ selectors, declarations: decl });
  }
  return rules;
}

function inlineStylesFromStylesheets(doc: Document): void {
  const styleEls = Array.from(doc.querySelectorAll('style'));
  for (const el of styleEls) {
    const text = el.textContent || '';
    for (const { selectors, declarations } of extractCssRules(text)) {
      for (const sel of selectors) {
        try {
          const nodes = doc.querySelectorAll(sel);
          for (let j = 0; j < nodes.length; j++) {
            const node = nodes[j] as HTMLElement;
            if (!node.getAttribute) continue;
            for (const [prop, val] of parseDeclarations(declarations)) {
              const cur = node.getAttribute('style');
              node.setAttribute('style', mergeInlineStyle(cur, prop, val));
            }
          }
        } catch {
          /* invalid selector */
        }
      }
    }
  }
}

function stripClassIdFromDocument(doc: Document): void {
  const walk = doc.getElementsByTagName('*');
  for (let i = 0; i < walk.length; i++) {
    const el = walk[i] as HTMLElement;
    el.removeAttribute('class');
    el.removeAttribute('id');
  }
}

function stripFormsFromDocument(doc: Document): void {
  const forms = Array.from(doc.querySelectorAll('form'));
  for (const form of forms) {
    const parent = form.parentNode;
    if (!parent) continue;
    while (form.firstChild) parent.insertBefore(form.firstChild, form);
    parent.removeChild(form);
  }
}

function stripListedPropsFromDocument(doc: Document, strip: string[]): void {
  if (!strip.length) return;
  const walk = doc.getElementsByTagName('*');
  for (let i = 0; i < walk.length; i++) {
    const el = walk[i] as HTMLElement;
    const st = el.getAttribute('style');
    if (!st) continue;
    const next = removePropsFromStyle(st, strip);
    if (next) el.setAttribute('style', next);
    else el.removeAttribute('style');
  }
}

export function transformForClient(html: string, client: ClientDef): string {
  let markup = html;
  if (client.unsupported.msoConditionals) markup = processMsoHtml(markup);

  const parser = new DOMParser();
  const doc = parser.parseFromString(markup, 'text/html');
  const u = client.unsupported;

  const stripFf = !!u.stripFontFace;
  const stripMq = !!u.stripMediaQueries;

  if (u.stripStyles) {
    const styleEls = Array.from(doc.querySelectorAll('style'));
    for (const el of styleEls) {
      const t = el.textContent || '';
      el.textContent = stripAtRulesFromStyleCss(t, stripFf, stripMq);
    }
    inlineStylesFromStylesheets(doc);
    for (const el of Array.from(doc.querySelectorAll('style'))) el.remove();
  } else {
    for (const el of Array.from(doc.querySelectorAll('style'))) {
      const t = el.textContent || '';
      const next = stripAtRulesFromStyleCss(t, stripFf, stripMq);
      if (next.trim()) el.textContent = next;
      else el.remove();
    }
  }

  if (u.stripClassId) stripClassIdFromDocument(doc);
  if (u.stripForms) stripFormsFromDocument(doc);
  if (u.stripProps?.length) stripListedPropsFromDocument(doc, u.stripProps);

  if (client.id === 'outlook') {
    const head = doc.head || doc.querySelector('head');
    if (head) head.insertAdjacentHTML('afterbegin', OUTLOOK_SIM_CSS);
  }

  return doc.documentElement ? doc.documentElement.outerHTML : markup;
}

export function detectUsedFeatures(html: string): string[] {
  const found: string[] = [];
  if (/@media\b/i.test(html)) found.push('@media');
  if (/@font-face\b/i.test(html)) found.push('@font-face');
  if (/border-radius\s*:/i.test(html)) found.push('border-radius');
  if (/box-shadow\s*:/i.test(html)) found.push('box-shadow');
  if (/background(?:-image)?\s*:\s*[^;]*gradient/i.test(html)) found.push('gradient-background');
  if (/\bflex\s*:|display\s*:\s*flex\b/i.test(html)) found.push('flex');
  if (/\bgrid\s*:|display\s*:\s*grid\b/i.test(html)) found.push('grid');
  return found;
}

function clientWarnsForFeatures(client: ClientDef, features: string[]): string[] {
  const u = client.unsupported;
  const warns: string[] = [];
  const add = (s: string) => {
    if (!warns.includes(s)) warns.push(s);
  };
  for (const f of features) {
    if (f === '@media' && u.stripMediaQueries) add(`@${f.slice(1)} queries may be removed in ${client.label}`);
    if (f === '@font-face' && u.stripFontFace) add('Custom @font-face may not load');
    if (f === 'border-radius' && u.stripProps?.some((p) => p.toLowerCase() === 'border-radius'))
      add('border-radius may be ignored');
    if (f === 'box-shadow' && u.stripProps?.some((p) => p.toLowerCase() === 'box-shadow'))
      add('box-shadow may be ignored');
    if (f === 'gradient-background' && u.stripProps?.some((p) => p.toLowerCase() === 'background-image'))
      add('CSS gradients / background-image may be stripped');
    if (f === 'flex' && u.stripProps?.some((p) => p.toLowerCase() === 'flex')) add('Flexbox may not layout as expected');
    if (f === 'grid' && u.stripProps?.some((p) => p.toLowerCase() === 'grid')) add('Grid may not layout as expected');
  }
  if (u.stripStyles) add('Embedded style tags may be inlined or dropped');
  if (u.stripClassId) add('class/id selectors may not apply as in the editor');
  if (u.stripForms) add('Forms may be flattened');
  if (u.msoConditionals) add('MSO conditional regions were adjusted for this preview');
  return warns;
}

export interface FtpPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  getHtml: () => string;
  /**
   * Omit to hide the Optimize action. It rewrites the raw HTML source, which
   * only the HTML editor can act on — the DnD editor owns a model that the
   * compiler regenerates, so there is nothing for it to optimize.
   */
  onOptimize?: () => void;
  variables?: Record<string, string>;
}

export function FtpPreviewModal({
  isOpen,
  onClose,
  getHtml,
  onOptimize,
  variables,
}: FtpPreviewModalProps) {
  const [activeClient, setActiveClient] = useState('browser');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'iphone' | 'android'>('desktop');
  const [previewHtml, setPreviewHtml] = useState('');
  const [featureSource, setFeatureSource] = useState('');

  const clientDef = useMemo(
    () => CLIENTS.find((c) => c.id === activeClient) ?? CLIENTS[0],
    [activeClient],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps -- editor HTML is read when the modal opens or the client tab changes; omit getHtml to avoid constant refreshes
  useEffect(() => {
    if (!isOpen) return;
    const sub = substituteVariables(getHtml(), variables);
    setFeatureSource(sub);
    setPreviewHtml(transformForClient(sub, clientDef));
  }, [isOpen, clientDef, variables]);

  const features = useMemo(() => detectUsedFeatures(featureSource), [featureSource]);
  const warnMessages = useMemo(() => clientWarnsForFeatures(clientDef, features), [clientDef, features]);

  if (!isOpen) return null;

  const deviceWrapClass =
    previewDevice === 'desktop'
      ? 'ftp-preview-frame-wrap'
      : `ftp-preview-frame-wrap ftp-preview-device ftp-preview-${previewDevice === 'iphone' ? 'iphone' : 'android'}`;

  return createPortal(
    <div
      className="ftp-modal-overlay active"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="ftp-modal ftp-preview-modal"
        role="dialog"
        aria-labelledby="ftp-preview-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ftp-modal-header">
          <h3 id="ftp-preview-modal-title">Email Client Preview</h3>
          <div className="ftp-preview-header-actions">
            {onOptimize && (
              <button type="button" className="ftp-preview-optimize-btn" onClick={onOptimize}>
                <Sparkles aria-hidden />
                Optimize
              </button>
            )}
            <button type="button" className="ftp-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>

        <div className="ftp-preview-client-bar">
          <div className="ftp-preview-client-tabs">
            {CLIENTS.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`ftp-preview-client-btn${activeClient === c.id ? ' active' : ''}`}
                data-client={c.id}
                onClick={() => setActiveClient(c.id)}
              >
                <c.Icon aria-hidden />
                {c.label}
              </button>
            ))}
          </div>
          <div className="ftp-preview-device-toggle">
            {(
              [
                ['desktop', 'Desktop', Monitor],
                ['iphone', 'iPhone', Smartphone],
                ['android', 'Android', Smartphone],
              ] as const
            ).map(([id, label, DeviceIcon]) => (
              <button
                key={id}
                type="button"
                className={`ftp-preview-device-btn${previewDevice === id ? ' active' : ''}`}
                onClick={() => setPreviewDevice(id)}
              >
                <DeviceIcon aria-hidden />
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="ftp-preview-body">
          <div className={deviceWrapClass}>
            <iframe className="ftp-preview-frame" title="Preview" srcDoc={previewHtml} />
          </div>
        </div>

        <div className="ftp-preview-info">
          {warnMessages.length ? (
            <>
              <div className="ftp-preview-info-title">
                <AlertTriangle aria-hidden />
                Client compatibility
              </div>
              <ul className="ftp-preview-warn-list">
                {warnMessages.map((w) => (
                  <li key={w} className="ftp-preview-warn-item ftp-preview-warn-active">
                    <Circle aria-hidden />
                    {w}
                  </li>
                ))}
              </ul>
            </>
          ) : features.length ? (
            <>
              <div className="ftp-preview-info-title">
                <Info aria-hidden />
                Detected features
              </div>
              <ul className="ftp-preview-warn-list">
                {features.map((f) => (
                  <li key={f} className="ftp-preview-warn-item">
                    <Circle aria-hidden />
                    {f}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="ftp-preview-info-ok">
              <CheckCircle2 aria-hidden />
              No advanced CSS features detected in this preview.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
